import { Component, OnInit, ViewChild } from '@angular/core';
import { DeprecatedConfirmationDialogComponent } from '@app/components';
import { jsonApiStructure } from '@app/functions';
import { SettingsMenuService } from '@app/modules/settings/services/settings-menu.service';
import { AuthService } from '@app/services/auth.service';
import { NotifyService } from '@app/services/notify.service';
import { SubscriptionCancellationForm } from '@forms/settings/components';
import { ModuleSelectorForm } from '@forms/settings/components/billing/module-selector/module-selector.form';
import { PaymentInformationForm } from '@forms/settings/components/billing/payment-information/payment-information.form';
import { Card } from '@models/settings/billing/card.model';
import { Customer } from '@models/settings/billing/customer.model';
import { Invoice } from '@models/settings/billing/invoice.model';
import { ProductPlan } from '@models/settings/billing/product-plan.model';
import { Product } from '@models/settings/billing/product.model';
import { SubscriptionItem } from '@models/settings/billing/subscription-item.model';
import { Subscription } from '@models/settings/billing/subscription.model';
import { BillingService } from '@settings/services';

@Component({
    templateUrl: './billing-show.template.html',
    styleUrls: ['./billing-show.style.scss'],
})
export class BillingShowView implements OnInit {
    @ViewChild('confirmation', { static: true }) confirmation!: DeprecatedConfirmationDialogComponent;
    subscriptions: Subscription[] = [];
    customer: Customer | null = new Customer();
    products: Product[] = [];
    cards: Card[] | null = null;
    invoices: Invoice[] | null = null;

    constructor(
        public auth: AuthService,
        public notify: NotifyService,
        private billingService: BillingService,
        public settingsMenuService: SettingsMenuService
    ) {}

    ngOnInit(): void {
        Customer.show()
            .then((customer) => {
                this.customer = customer;
                if (customer) {
                    this.loadProducts();
                    this.loadSubscriptions();
                    this.loadCards();
                    this.loadInvoices();
                }
            })
            .catch(() => {
                this.customer = null;
            });
    }

    loadCards(): void {
        this.cards = null;
        Card.get()
            .then(([cards]) => {
                this.cards = cards;
            })
            .catch(this.notify.error);
    }

    loadInvoices(): void {
        this.invoices = null;
        Invoice.get().then(([invoices]) => {
            this.invoices = invoices;
        });
    }

    loadProducts(): void {
        Product.get()
            .then(([products]) => {
                this.products = products;
                this.loadProductPlans();
            })
            .catch(this.notify.error);
    }

    loadProductPlans(): void {
        this.products.forEach((product) => {
            ProductPlan.param('billingProduct', product.identifier)
                .get()
                .then(([productPlans]) => {
                    product.productPlans = productPlans;
                })
                .catch(this.notify.error);
        });
    }

    async loadSubscriptions(): Promise<void> {
        try {
            let [subscriptions] = await Subscription.get();
            if (!subscriptions.length) {
                subscriptions = await this.createDefaultSubscription();
            }
            // Load items before setting the subscriptions property
            await Promise.all(subscriptions.map(this.loadSubscriptionItem));
            this.subscriptions = subscriptions;
        } catch (error) {
            this.notify.error(error);
        }
    }

    async loadSubscriptionItem(subscription: Subscription): Promise<void> {
        try {
            const [subscriptionItems] = await SubscriptionItem.param(
                'billingSubscription',
                subscription.identifier
            ).get();
            subscription.subscriptionItems = subscriptionItems;
        } catch (error) {
            this.notify.error(error);
        }
    }

    async createDefaultSubscription(): Promise<Subscription[]> {
        const subscription = await new Subscription().save();
        this.loadInvoices();

        return [subscription];
    }

    onPaymentInformationSave(paymentInformationForm: PaymentInformationForm): void {
        this.cards = null;
        paymentInformationForm.retrieveToken().then((token) => {
            if (typeof token === 'string') {
                this.notify.error(token);
                return;
            }

            token
                .save()
                .then((card) => {
                    paymentInformationForm.reset();
                    this.notify.success({
                        key: 'settings.payment-information.added',
                        params: { last4Digits: card.last4 },
                    });
                    this.loadCards();
                })
                .catch(this.notify.error);
        });
    }

    onModuleSelectorSave(moduleSelectorForm: ModuleSelectorForm): void {
        const planIdentifiers = moduleSelectorForm.selectedProductPlans;

        moduleSelectorForm.reset();

        // Always apply to the first subscription, we want to avoid multiple eventually
        const subscriptionId = this.subscriptions[0].identifier;

        this.subscriptions = [];

        const subscriptionItemPromises = planIdentifiers.map((planIdentifier) =>
            new SubscriptionItem({ plan: planIdentifier }).param('billingSubscription', subscriptionId).save()
        );

        Promise.all(subscriptionItemPromises)
            .then(this.auth.hydrate)
            .then(this.loadSubscriptions)
            .catch(this.notify.error);
    }

    onCancelSubscription(subscriptionCancellationForm: SubscriptionCancellationForm): void {
        const reason = subscriptionCancellationForm.reason;
        const note = subscriptionCancellationForm.note;

        const reasonPayload = jsonApiStructure('subscriptionCancellation', { reason, note });

        this.billingService
            .cancelSubscription(reasonPayload)
            .then(() => {
                this.notify.success('settings.subscription-cancellation.requestSubmitted');
            })
            .catch(this.notify.error);

        subscriptionCancellationForm.reset();
    }

    onCancelModule(item: SubscriptionItem): void {
        this.confirmation.show().then((confirm) => {
            if (confirm) {
                this.billingService
                    .cancelPlan(item.planIdentifier)
                    .then(() => {
                        this.notify.success('settings.subscription-cancellation.requestSubmitted');
                    })
                    .catch(this.notify.error);
            }
        });
    }
}
